'use client';

import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useStore } from '@/lib/store';
import { getRecommendationEngine, ContentItem } from '@/lib/recommendation-engine';
import { MediaCard } from '@/components/MediaCard';
import { Skeleton } from '@/components/ui/skeleton';
import { Heart, Film, Tv, Star, Sparkles } from 'lucide-react';

interface PersonalizedRowData {
  id: string;
  title: string;
  items: ContentItem[];
  icon: React.ReactNode;
}

export function DynamicPersonalizedRows() {
  const { user } = useAuth();
  const { watchlist, dislikedItems } = useStore();
  const [rows, setRows] = useState<PersonalizedRowData[]>([]);
  const [loading, setLoading] = useState(true);
  const hasLoadedRef = useRef(false);
  const prevWatchlistLengthRef = useRef(watchlist.length);

  useEffect(() => {
    if (!user || watchlist.length === 0) {
      setLoading(false);
      return;
    }

    // Only load on initial mount or if watchlist changed significantly
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadPersonalizedRows();
      return;
    }

    const watchlistDiff = Math.abs(watchlist.length - prevWatchlistLengthRef.current);
    if (watchlistDiff > 2) {
      prevWatchlistLengthRef.current = watchlist.length;
      loadPersonalizedRows();
    }
  }, [user, watchlist.length]);

  const loadPersonalizedRows = async () => {
    if (!user || watchlist.length === 0) return;

    setLoading(true);
    try {
      const engine = getRecommendationEngine();
      await engine.initialize(user.uid, watchlist, dislikedItems);

      const newRows: PersonalizedRowData[] = [];

      // 1. Get "Because you like [Genre]" rows (top 2 genres)
      const topGenres = engine.getTopGenres(watchlist, 2);
      for (const genre of topGenres) {
        if (genre.count >= 2) { // Only show if user has at least 2 items in this genre
          const items = await engine.getGenreBasedRecommendations(genre.id, genre.name, watchlist);
          if (items.length >= 5) {
            newRows.push({
              id: `genre-${genre.id}`,
              title: `Because You Like ${genre.name}`,
              items: items.slice(0, 20),
              icon: <Heart className="h-6 w-6 text-pink-500" />,
            });
          }
        }
      }

      // 2. Get "Because you watched [Show/Movie]" rows (2-3 random items)
      const randomItems = engine.getRandomWatchedItems(watchlist, 3);
      for (const item of randomItems) {
        const similarItems = await engine.getSimilarToWatched(item);
        if (similarItems.length >= 5) {
          newRows.push({
            id: `similar-${item.id}`,
            title: `Because You Watched ${item.name}`,
            items: similarItems.slice(0, 20),
            icon: item.type === 'tv'
              ? <Tv className="h-6 w-6 text-blue-500" />
              : <Film className="h-6 w-6 text-purple-500" />,
          });
        }
      }

      // 3. Get "Based on your highly rated" row
      const highlyRatedRecs = await engine.getBasedOnHighlyRated(watchlist);
      if (highlyRatedRecs.length >= 5) {
        newRows.push({
          id: 'highly-rated',
          title: 'Based On Your Top Rated',
          items: highlyRatedRecs.slice(0, 20),
          icon: <Star className="h-6 w-6 text-yellow-500" />,
        });
      }

      // Shuffle rows for variety (but keep first genre row at top)
      if (newRows.length > 2) {
        const firstRow = newRows[0];
        const otherRows = newRows.slice(1).sort(() => Math.random() - 0.5);
        setRows([firstRow, ...otherRows]);
      } else {
        setRows(newRows);
      }
    } catch (error) {
      console.error('Error loading personalized rows:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user || watchlist.length < 3) {
    return null; // Don't show personalized rows if user has less than 3 items
  }

  if (loading && rows.length === 0) {
    return (
      <div className="space-y-12">
        {[1, 2].map((i) => (
          <div key={i} className="space-y-4">
            <div className="flex items-center gap-3">
              <Skeleton className="h-6 w-6 rounded-full" />
              <Skeleton className="h-7 w-64" />
            </div>
            <div className="flex gap-4 overflow-hidden">
              {Array.from({ length: 6 }).map((_, j) => (
                <Skeleton key={j} className="w-40 md:w-48 aspect-[2/3] flex-shrink-0 rounded-lg" />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="space-y-12">
      {rows.map((row) => (
        <PersonalizedRowSection key={row.id} row={row} />
      ))}
    </div>
  );
}

function PersonalizedRowSection({ row }: { row: PersonalizedRowData }) {
  const rowId = useRef(`row-${row.id}-${Math.random().toString(36).substr(2, 9)}`).current;

  // Filter items with valid poster paths
  const validItems = row.items.filter(item => item.poster_path && item.poster_path.trim() !== '');

  if (validItems.length < 5) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        {row.icon}
        <h2 className="text-2xl md:text-3xl font-bold text-white">{row.title}</h2>
      </div>

      {/* Scrollable content */}
      <div className="overflow-x-auto pb-4 -mx-4 px-4 md:mx-0 md:px-0">
        <div className="flex gap-3 md:gap-4" style={{ width: 'max-content' }}>
          {validItems.map((item, index) => (
            <MediaCard
              key={`${rowId}-${item.type}-${item.id}-${index}`}
              item={item}
              showAddButton={true}
              size="medium"
            />
          ))}
        </div>
      </div>
    </div>
  );
}
